import { useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { api } from "@/lib/api";
import Landing from "@/pages/Landing";
import CustomerDashboard from "@/pages/CustomerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Memberships from "@/pages/Memberships";
import Classes from "@/pages/Classes";

function AuthCallback() {
  const processed = useRef(false);
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
        await checkAuth();
        window.history.replaceState({}, "", "/dashboard");
        navigate("/dashboard", { replace: true });
      } catch {
        setError("Sign-in failed. Please try again.");
        setTimeout(() => navigate("/", { replace: true }), 2000);
      }
    })();
  }, [checkAuth, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="font-heading text-xl" data-testid="auth-callback-status">
        {error || "Signing you in…"}
      </p>
    </div>
  );
}

function DashboardRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-heading text-xl">Loading…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return user.role === "admin" ? <AdminDashboard /> : <CustomerDashboard />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/memberships" element={<Memberships />} />
      <Route path="/classes" element={<Classes />} />
      <Route path="/dashboard" element={<DashboardRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
